import { useAuth } from "context/AuthProvider";
import React, { useEffect, useState } from "react";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Table,
} from "reactstrap";
import { apiEmpleadosService } from "services/apiEmpleadosService";
import { apiFichajesService } from "services/apiFichajesService";

const ESTADOS = {
  TRABAJANDO: "TRABAJANDO",
  FUERA: "FUERA",
  SIN_FICHAJE: "SIN_FICHAJE",
};

const PRIORIDAD_ESTADO = {
  [ESTADOS.TRABAJANDO]: 0,
  [ESTADOS.SIN_FICHAJE]: 1,
  [ESTADOS.FUERA]: 2,
};

const ControlAsistenciaHoy = () => {
  const { empresa } = useAuth();
  const [asistenciasHoy, setAsistenciasHoy] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const maxPagesToShow = 5;

  useEffect(() => {
    const fetchData = async () => {
      if (!empresa?.id) {
        return;
      }

      const hoy = new Date().toISOString().split("T")[0];

      const [empleadosResp, fichajesResp] = await Promise.all([
        apiEmpleadosService.getAllFiltered(
          empresa.id,
          null,
          null,
          null,
          hoy,
          "asc",
          0,
          1000
        ),
        apiFichajesService.getFichajesEmpresaByDate(empresa.id, hoy, hoy),
      ]);

      const empleadosActivos = empleadosResp?.content || [];

      const ultimoFichajePorEmpleado = new Map();
      (fichajesResp || []).forEach((fichaje) => {
        const actual = ultimoFichajePorEmpleado.get(fichaje.empleadoId);
        const horaActual = actual ? new Date(actual.horaFichaje).getTime() : 0;
        const horaNueva = new Date(fichaje.horaFichaje).getTime();
        if (!actual || horaNueva > horaActual) {
          ultimoFichajePorEmpleado.set(fichaje.empleadoId, fichaje);
        }
      });

      const data = empleadosActivos
        .map((empleado) => {
          const ultimoFichaje = ultimoFichajePorEmpleado.get(empleado.id);
          const tipo = ultimoFichaje?.tipo?.toLowerCase();

          let estado = ESTADOS.SIN_FICHAJE;
          if (tipo === "entrada") {
            estado = ESTADOS.TRABAJANDO;
          } else if (tipo === "salida") {
            estado = ESTADOS.FUERA;
          }

          return {
            empleadoId: empleado.id,
            empleado: `${empleado.apellidos}, ${empleado.nombre}`,
            horaFichaje: ultimoFichaje?.horaFichaje || null,
            tipoFichaje: tipo || null,
            estado,
          };
        })
        .sort((a, b) => {
          const byEstado = PRIORIDAD_ESTADO[a.estado] - PRIORIDAD_ESTADO[b.estado];
          if (byEstado !== 0) {
            return byEstado;
          }
          const horaA = a.horaFichaje ? new Date(a.horaFichaje).getTime() : 0;
          const horaB = b.horaFichaje ? new Date(b.horaFichaje).getTime() : 0;
          return horaB - horaA;
        });

      setAsistenciasHoy(data);
    };

    fetchData();
  }, [empresa?.id]);

  const totalPages = Math.max(1, Math.ceil(asistenciasHoy.length / pageSize));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const start = (page - 1) * pageSize;
  const asistenciasPaginadas = asistenciasHoy.slice(start, start + pageSize);

  const goToPage = (nextPage) => {
    const clamped = Math.min(Math.max(nextPage, 1), totalPages);
    setPage(clamped);
  };

  const getVisiblePages = () => {
    if (totalPages <= maxPagesToShow) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const half = Math.floor(maxPagesToShow / 2);
    let startPage = page - half;
    let endPage = page + half;
    if (maxPagesToShow % 2 === 0) {
      endPage -= 1;
    }
    if (startPage < 1) {
      startPage = 1;
      endPage = maxPagesToShow;
    }
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = totalPages - maxPagesToShow + 1;
    }
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  const formatHora = (value) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getEstadoMeta = (estado) => {
    if (estado === ESTADOS.TRABAJANDO) {
      return { color: "success", text: "Ha entrado" };
    }
    if (estado === ESTADOS.FUERA) {
      return { color: "warning", text: "Ha salido" };
    }
    return { color: "secondary", text: "No ha fichado" };
  };

  const totales = asistenciasHoy.reduce(
    (acc, item) => {
      if (item.estado === ESTADOS.TRABAJANDO) acc.trabajando += 1;
      if (item.estado === ESTADOS.FUERA) acc.fuera += 1;
      if (item.estado === ESTADOS.SIN_FICHAJE) acc.sinFichaje += 1;
      return acc;
    },
    { trabajando: 0, fuera: 0, sinFichaje: 0 }
  );

  return (
    <Card>
      <CardHeader>Control de asistencia de hoy</CardHeader>
       <Row className="mb-3 text-center">
          <Col xs="4">
            <div className="h2 mb-0 text-success">{totales.trabajando}</div>
            <small className="text-muted">Trabajando</small>
          </Col>
          <Col xs="4">
            <div className="h2 mb-0 text-warning">{totales.fuera}</div>
            <small className="text-muted">Fuera</small>
          </Col>
          <Col xs="4">
            <div className="h2 mb-0 ">{totales.sinFichaje}</div>
            <small className="text-muted">Sin fichar</small>
          </Col>
        </Row>

        <Table className="text-center table-flush" responsive>
          <thead className="thead-light">
            <tr>
              <th className="text-left">Empleado</th>
              <th>Estado actual</th>
              <th>Último fichaje</th>
            </tr>
          </thead>
          <tbody>
            {asistenciasHoy.length === 0 && (
              <tr>
                <td colSpan="3" className="text-muted">
                  No hay empleados activos hoy
                </td>
              </tr>
            )}
            {asistenciasPaginadas.map((item) => {
              const estadoMeta = getEstadoMeta(item.estado);
              return (
                <tr key={item.empleadoId}>
                  <td className="text-left font-weight-600">{item.empleado}</td>
                  <td>
                    <Badge color={estadoMeta.color} pill>
                      {estadoMeta.text}
                    </Badge>
                  </td>
                  <td>{formatHora(item.horaFichaje)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {asistenciasHoy.length > pageSize && (
          <div className="d-flex justify-content-center p-3">
            <Pagination aria-label="Paginación de asistencia">
              <PaginationItem disabled={page === 1}>
                <PaginationLink previous onClick={() => goToPage(page - 1)} />
              </PaginationItem>
              {getVisiblePages().map((pageNumber) => (
                  <PaginationItem active={pageNumber === page} key={pageNumber}>
                    <PaginationLink onClick={() => goToPage(pageNumber)}>
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
              ))}
              <PaginationItem disabled={page === totalPages}>
                <PaginationLink next onClick={() => goToPage(page + 1)} />
              </PaginationItem>
            </Pagination>
          </div>
        )}
    </Card>
  );
};

export default ControlAsistenciaHoy;
